import { fetchCategorySuggestionsNewMLModel, isBookkeepingHeroAllowed, MIN_INPUT_SIGNS } from '@components'

import { trackError } from '@billy/track-error'
import { v4 as uuidv4 } from 'uuid'

import type { Contact } from '@features/contacts/types/contact'
import { ChartOfAccountId } from '@modules-deprecated/app/organization/enums/chartOfAccountId'

import { Scope } from '../../../../../enums/scope'
import { LocaleIdentifier } from '../../../../../types/localeIdentifier'
import { getApiCountryKey } from '../../../../../utils/getApiCountryKey'
import { isAuthorized } from '../../../../../utils/isAuthorized'
import { getBookkeepingTagsFromTagScores } from './getBookkeepingTagsFromTagScores'

const TAG_SUGGESTIONS_AMOUNT = 3

interface GetBookkeepingTagSuggestionsFromDescriptionProps {
  chartOfAccountId?: ChartOfAccountId
  contact?: Partial<Contact>
  description?: string
  locale: string
  organizationCountryCode?: string
  organizationId: string
  userId?: string
  userLocale?: LocaleIdentifier
}

export const getBookkeepingTagSuggestionsFromDescription = async ({
  chartOfAccountId,
  contact,
  description,
  locale,
  organizationCountryCode,
  organizationId,
  userId,
  userLocale,
}: GetBookkeepingTagSuggestionsFromDescriptionProps) => {
  const trimmedDescription = description?.trim()

  if (
    !trimmedDescription ||
    trimmedDescription.length < MIN_INPUT_SIGNS ||
    !chartOfAccountId ||
    !isBookkeepingHeroAllowed(chartOfAccountId) ||
    !userId ||
    !userLocale ||
    !isAuthorized(Scope.BillWriteCategorySearch)
  ) {
    return []
  }

  try {
    /* eslint-disable @typescript-eslint/naming-convention */
    const { suggested } = await fetchCategorySuggestionsNewMLModel({
      contact_country: contact?.countryId || '',
      contact_reg_number: contact?.registrationNo || '',
      contact_vat_number: contact?.vatNo || '',
      isModelSearchEnabled: true,
      meta: {
        category_id: null,
        file_id: null,
        user_id: userId,
      },
      organisation_country_code: getApiCountryKey(organizationCountryCode).toUpperCase(),
      organization_id: organizationId,
      scanned_category_result: null,
      session_id: uuidv4(),
      text_input: trimmedDescription,
      user_locale: userLocale,
    })
    /* eslint-enable @typescript-eslint/naming-convention */

    const tagScores = suggested.slice(0, TAG_SUGGESTIONS_AMOUNT).map((suggestion) => ({
      score: suggestion.score,
      tagId: String(suggestion.id),
      tagName: suggestion.name,
    }))

    if (!tagScores.length) {
      return []
    }

    return getBookkeepingTagsFromTagScores(tagScores, locale, organizationId)
  } catch (error) {
    trackError(error)
    return []
  }
}
